/**
 * Orden Payment Utilities
 * Pure functions for payment logic and tipo_cobro badge styling
 */

import type { EstadoOrden } from "@/hooks/use-ordenes";

/**
 * Determina si se puede realizar un pago directo en una orden.
 * Permite pagar si: no es FACTURA, no está en BAJA, no está completamente pagada,
 * no es cortesía, y el total es mayor a cero.
 * Soporta pagos parciales: tipo_cobro=DIRECTO con pagada=false significa abono pendiente.
 */
export function canPagar(
  tipoCobro: string | null,
  estado: EstadoOrden,
  pagada?: boolean,
  total?: string,
  esCortesia?: boolean
): boolean {
  if (estado === "BAJA") return false;
  if (tipoCobro === "FACTURA") return false;
  if (pagada === true) return false;
  if (esCortesia === true) return false;
  // Órdenes con total=0 no se pueden pagar (son candidatas a cortesía)
  if (total !== undefined && parseFloat(total) === 0) return false;
  return true;
}

/**
 * Determina si se puede anular el pago directo de una orden.
 * Solo cuando hay pagos directos registrados (tipo_cobro DIRECTO) y no es cortesía.
 */
export function canAnularPago(
  tipoCobro: string | null,
  estado: EstadoOrden,
  esCortesia?: boolean
): boolean {
  if (esCortesia === true) return false;
  return tipoCobro === "DIRECTO" && estado !== "BAJA";
}

/**
 * Obtiene el variant del Badge según el tipo de cobro
 * @param tipoCobro - Tipo de cobro (null, "DIRECTO", "FACTURA")
 * @returns variant del Badge de Shadcn/ui
 */
export function getTipoCobroBadgeVariant(
  tipoCobro: string | null
): "outline" | "default" | "secondary" {
  if (tipoCobro === null) return "outline";
  if (tipoCobro === "DIRECTO") return "default";
  return "secondary";
}

/**
 * Obtiene la clase CSS del Badge según el tipo de cobro
 * @param tipoCobro - Tipo de cobro (null, "DIRECTO", "FACTURA")
 * @returns clase CSS para el Badge
 */
export function getTipoCobroBadgeClass(tipoCobro: string | null): string {
  if (tipoCobro === null) return "border-gray-400 text-gray-700";
  if (tipoCobro === "DIRECTO") return "bg-green-600 hover:bg-green-700";
  return "bg-blue-600 hover:bg-blue-700";
}

/**
 * Determina si una orden puede ser marcada como cortesía.
 * Condiciones: total == 0, no pagada, no en BAJA, no cobrada por FACTURA.
 */
export function canMarcarCortesia(
  tipoCobro: string | null,
  estado: EstadoOrden,
  pagada: boolean,
  total: string
): boolean {
  if (estado === "BAJA") return false;
  if (tipoCobro === "FACTURA") return false;
  if (pagada) return false;
  return parseFloat(total) === 0;
}
